import React, { useEffect, useState, useCallback } from "react";
import styles from "./CartSummary.module.scss";
import classnames from "classnames";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import DistributorLogo from "@src/components/common/DistributorLogo";
import SpinningLoader from "@src/components/common/SpinningLoader";
import * as SavingsAndRewardsTypes from "@src/components/widgets/SavingsAndRewardsTile/SavingsAndRewardsTypes";
import { Link } from "react-router-dom";
import ProductImage from "../ProductImage";
import { useCartStore } from "@src/state/cartState";
import ProductDescription from "../ProductDescription";
import { CartCount, useToast } from "@swordsweeper/ssi-ui-component-library";
import debounce from "lodash/debounce";
import * as CartApi from "@src/api/CartApi";

const NUMBER_OF_PREVIEW_PRODUCTS_PER_PRODUCT_GROUPING = 3;

export default function CartSummary() {
  const { showToast } = useToast();
  const { cart, loading: isLoadingCart, setCart } = useCartStore();
  const [isLoading, setIsLoading] = useState(false);

  const debouncedUpdateCartProductQuantity = useCallback(
    debounce(async (cartDistributorProductId: string, newCount: number) => {
      if (newCount === 0) {
        handleRemoveProductFromCart(cartDistributorProductId);
      } else {
        setIsLoading(true);
        try {
          const response = await CartApi.updateCartProductQuantity(
            cartDistributorProductId,
            newCount,
          );
          setCart(response.body);
        } catch (e) {
          console.error(e);
          showToast({
            message: "Error updating quantity",
            variant: "error",
          });
        } finally {
          setIsLoading(false);
        }
      }
    }, 500),
    [setCart, showToast],
  );

  const handleUpdateCount = async (
    cartDistributorProductId: string,
    newCount: number,
  ) => {
    if (!isLoading) {
      debouncedUpdateCartProductQuantity(cartDistributorProductId, newCount);
    }
  };

  // Cancel pending debounced updates on component unmount
  useEffect(() => {
    return () => {
      debouncedUpdateCartProductQuantity.cancel();
    };
  }, [debouncedUpdateCartProductQuantity]);

  const handleRemoveProductFromCart = async (
    cartDistributorProductId: string,
  ) => {
    // Cancel any pending debounced quantity updates to prevent race condition
    debouncedUpdateCartProductQuantity.cancel();

    setIsLoading(true);
    try {
      const response = await CartApi.removeCartProduct(
        cartDistributorProductId,
      );
      setCart(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error removing product from cart",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      {isLoadingCart && <SpinningLoader />}

      <div className="font-weight-bold">Cart</div>

      {!!cart && (
        <div>
          <div className="flex-row spaced-content text-xs border-bottom padding-y-xs">
            <span className="font-weight-bold">
              <PriceDisplay amount={cart.subtotal} /> Subtotal
            </span>
            <span>|</span>
            <span>
              {cart.itemCount} item{cart.itemCount !== 1 && "s"}
            </span>
          </div>

          {/* Note: Commented out for Beta due to missing functionality */}
          {/* <div className="padding-y-md">
            <div>With this order, you're getting:</div>
            <div className="padding-y-xs spaced-content-v">
              {cart.savingsAndRewards.map(
                ({ type, description }, savingsAndRewardsListItemIndex) => (
                  <div
                    key={`savings-and-rewards-list-item-${savingsAndRewardsListItemIndex}`}
                    className="flex-row flex-center flex-start"
                  >
                    <i {...SavingsAndRewardsTypes.PROPS_MAPPING[type]} />
                    <span className="margin-left-xs">{description}</span>
                  </div>
                ),
              )}
            </div>
          </div> */}

          <div className="flex-row spaced-content padding-bottom-md">
            <Link
              id="cart-summary-view-cart-link"
              className="btn btn-primary btn-bordered btn-md flex-1 text-center link"
              to="/cart"
            >
              <i className="fa-light fa-cart-shopping" />
              &nbsp; View Cart
            </Link>
          </div>

          {cart.distributorProductGroupings.map(
            (distributorProductGrouping) => {
              const allProducts = [
                ...(distributorProductGrouping.products?.backordered || []),
                ...(distributorProductGrouping.products?.available || []),
                ...(distributorProductGrouping.products?.priceUnavailable ||
                  []),
                ...(distributorProductGrouping.products?.stockUnavailable ||
                  []),
              ];
              const firstThreeProducts = allProducts.slice(
                0,
                NUMBER_OF_PREVIEW_PRODUCTS_PER_PRODUCT_GROUPING,
              );
              const otherProductCount =
                allProducts.length -
                NUMBER_OF_PREVIEW_PRODUCTS_PER_PRODUCT_GROUPING;
              return (
                <div
                  key={`cart-distributor-product-grouping-${distributorProductGrouping.distributorId}`}
                >
                  <div className="flex-row flex-center space-between padding-y-sm border-top border-bottom">
                    <DistributorLogo
                      distributorSlug={
                        distributorProductGrouping.distributorSlug
                      }
                      height={24}
                    />
                    <span className="text-xs">
                      Subtotal{" "}
                      <PriceDisplay
                        amount={distributorProductGrouping.subtotal}
                      />
                    </span>
                  </div>

                  {firstThreeProducts.map(
                    ({
                      id,
                      product,
                      livePricing,
                      quantity,
                      imageUrl,
                      thumbnailUrl,
                    }) => {
                      const isOutOfStock =
                        !livePricing || !livePricing.isInStock;
                      const imageSrc =
                        thumbnailUrl ?? imageUrl ?? product.thumbnailUrl;
                      return (
                        <div
                          key={`dpg-${distributorProductGrouping.id}-product-${product.id}`}
                          className={classnames(
                            "padding-y-xs flex-row flex-center",
                            { [styles.outOfStock]: isOutOfStock },
                          )}
                        >
                          <div className={styles.productImage}>
                            <ProductImage imageSrc={imageSrc} sizePx={60} />
                          </div>

                          <div className="margin-left-md">
                            <div className={styles.itemName}>
                              {product.itemName}
                            </div>
                            <ProductDescription
                              className={classnames(
                                "text-xs",
                                styles.description,
                              )}
                              maxWidth="150px"
                              maxLines={3}
                              description={product.description}
                            />
                            <div className="margin-top-xs">
                              <CartCount
                                onChange={(newCount: number) =>
                                  isOutOfStock
                                    ? null
                                    : handleUpdateCount(id, newCount)
                                }
                                onDelete={() => handleRemoveProductFromCart(id)}
                                value={isOutOfStock ? 1 : quantity}
                                maxValue={100000}
                                decrementButtonId={`cart-summary-decrement-button-${id}`}
                                valueInputId={`cart-summary-value-input-${id}`}
                                valueDisplayId={`cart-summary-value-display-${id}`}
                                incrementButtonId={`cart-summary-increment-button-${id}`}
                              />
                            </div>

                            {isOutOfStock && (
                              <div className="text-xs text-error text-italic font-weight-bold margin-top-xs">
                                Out of Stock
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    },
                  )}

                  {otherProductCount > 0 && (
                    <div className="text-center padding-y-sm text-sm">
                      <Link to="/cart">
                        +{otherProductCount} more item
                        {otherProductCount !== 1 && "s"}
                      </Link>
                    </div>
                  )}
                </div>
              );
            },
          )}

          {isLoading && <SpinningLoader whiteBackground />}
        </div>
      )}
    </div>
  );
}
